
import { useEffect, useRef, useState } from "react";
import HeroSection from "@/components/InterfaithDialogue/HeroSection";
import FaithSelector from "@/components/InterfaithDialogue/FaithSelector";
import ResponseDisplay from "@/components/InterfaithDialogue/ResponseDisplay";
import HeavenlyLoader from "@/components/HeavenlyLoader";
import { useInterfaithDialogue } from "@/components/InterfaithDialogue/useInterfaithDialogue";
import { useScrollToNewContent } from "@/hooks/useScrollToNewContent";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import FaithCards from "@/components/InterfaithDialogue/FaithCards";

const InterfaithDialogueTool = () => {
  const {
    selectedFaith,
    setSelectedFaith,
    question,
    setQuestion,
    isLoading,
    isSaving,
    response,
    error,
    handleSubmit,
    saveResponse,
    shareViaEmail,
    shareViaSMS,
    shareToFacebook
  } = useInterfaithDialogue();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Use hook to scroll to response when it's generated
  const responseRef = useScrollToNewContent(response, {
    behavior: "smooth",
    delay: 300,
    condition: !isLoading && !!response,
    offset: 100
  });

  // Handle when a sample question is selected
  const handleSampleQuestionSelect = (sampleQuestion: string) => {
    setQuestion(sampleQuestion);
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Hero Section */}
      <HeroSection />
      
      <div className="bg-heaven-accent/10 p-6 rounded-lg mb-8 border border-heaven-gold/20">
        <h3 className="text-xl font-bold mb-4">Interfaith Dialogue Tool</h3>
        <p className="mb-4">This tool helps you engage with other faiths and worldviews from a biblical perspective. Select a faith from the dropdown, choose one of the sample questions that appear, or write your own question.</p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6">
          <div>
            <h4 className="font-bold text-lg mb-3">About This Tool:</h4>
            <ul className="list-disc list-inside space-y-2 text-sm">
              <li>Addresses major world religions and worldviews</li>
              <li>Provides biblical responses to common claims</li>
              <li>Includes scripture references to support answers</li>
              <li>Helps you understand different beliefs while staying grounded in truth</li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-lg mb-3">How To Use:</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Select a faith or worldview from the dropdown</li>
              <li>Choose one of the sample questions that appear</li>
              <li>Or write your own question about that faith</li>
              <li>Submit your question to receive a biblical response</li>
            </ol>
          </div>
        </div>
      </div>
      
      {/* Faith Selection with Sample Questions */}
      <FaithSelector
        selectedFaith={selectedFaith || ""}
        onFaithChange={setSelectedFaith}
        onSampleQuestionSelect={handleSampleQuestionSelect}
      />
      
      {/* Question Form */}
      <div className="mb-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2">
              Your Question:
            </label>
            <Textarea
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Enter your question about an interfaith topic..."
              className="w-full min-h-[120px]"
              disabled={isLoading}
            />
          </div>
          
          <Button 
            type="submit" 
            disabled={isLoading || !selectedFaith || !question.trim()}
            variant="gold"  // Changed to gold variant
            className="w-full"
          >
            Get Biblical Response
          </Button>
        </form>
      </div>
      
      {/* Loading State */}
      {isLoading && <HeavenlyLoader message="Analyzing faith traditions and preparing a biblical response..." />}
      
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-300 text-red-800 p-4 rounded-md mb-6">
          <p>{error}</p>
        </div>
      )}
      
      {/* Response Display */}
      {response && !isLoading && (
        <div ref={responseRef}>
          <ResponseDisplay 
            response={response} 
            isSaving={isSaving}
            onSave={saveResponse}
            onShareEmail={shareViaEmail}
            onShareSMS={shareViaSMS}
            onShareFacebook={shareToFacebook}
            faithGroup={selectedFaith}
          />
        </div>
      )}
      
      {/* Faith Cards Section - Added here to ensure visibility */}
      <FaithCards />
    </div>
  );
};

export default InterfaithDialogueTool;
